import { useCallback, useMemo, useState } from 'react';
import { buildRangeRowsSchema } from '../schemas/parameters.schema';
import type { RangeCombination, RangesKind, StateReferenceDTO, StateReferenceRange } from '../types/parameters.types';
import {
	addRangeCombination,
	blankRangeRows,
	rowsFromRanges,
	updateRangeCombination,
	type RangeCombinationInput,
} from '../utils/rangeCombinations';

interface UseRangeCombinationFormParams {
	kind: RangesKind;
	reference: StateReferenceDTO;
	combinations: RangeCombination[];
	combination: RangeCombination | null;
	onSubmit: (profiles: StateReferenceRange[]) => Promise<boolean>;
	onClose: () => void;
}

export const useRangeCombinationForm = ({
	kind,
	reference,
	combinations,
	combination,
	onSubmit,
	onClose,
}: UseRangeCombinationFormParams) => {
	const key = kind === 'foliar' ? 'criticalRanges' : 'drisParams';
	const [input, setInput] = useState<RangeCombinationInput>(() => ({
		coffeeType: combination?.coffeeType ?? 'cafeArabica',
		mode: combination?.mode ?? 'general',
		region: 'norte',
		biennial: 'alta',
		season: 'inverno',
		rows: combination ? rowsFromRanges(combination.ranges) : blankRangeRows(kind),
	}));
	const [baseId, setBaseId] = useState('blank');
	const [error, setError] = useState<string | null>(null);

	const baseOptions = useMemo(
		() => [
			{ value: 'blank', label: 'Preencher manualmente' },
			...combinations.map((item) => ({ value: item.id, label: `${item.title} · ${item.context}` })),
		],
		[combinations]
	);

	const setField = useCallback(<K extends keyof RangeCombinationInput>(field: K, value: RangeCombinationInput[K]) => {
		setError(null);
		setInput((previous) => ({ ...previous, [field]: value }));
	}, []);

	const selectBase = useCallback(
		(id: string) => {
			setBaseId(id);
			const source = combinations.find((item) => item.id === id);
			setInput((previous) => ({
				...previous,
				rows: source ? rowsFromRanges(source.ranges) : blankRangeRows(kind),
			}));
		},
		[combinations, kind]
	);

	const setValue = useCallback((rowIndex: number, valueIndex: number, value: number | undefined) => {
		setError(null);
		setInput((previous) => ({
			...previous,
			rows: previous.rows.map((row, i) =>
				i === rowIndex ? { ...row, values: row.values.map((item, j) => (j === valueIndex ? value : item)) } : row
			),
		}));
	}, []);

	const submit = async () => {
		const parsed = buildRangeRowsSchema(kind).safeParse(input.rows);
		if (!parsed.success) {
			setError(parsed.error.issues[0].message);
			return;
		}
		if (combination) {
			if (await onSubmit(updateRangeCombination(reference, key, combination, input.rows))) onClose();
			return;
		}
		const result = addRangeCombination(reference[key], input);
		if (!result.ok) {
			setError(result.error);
			return;
		}
		if (await onSubmit(result.value)) onClose();
	};

	return { input, baseId, baseOptions, error, setField, selectBase, setValue, submit };
};
